/* eslint-disable react-hooks/exhaustive-deps */
// TODO: hook 개별 파일로 분리하도록 리팩토링
'use client';

import { debounce, throttle } from 'lodash';
import type React from 'react';
import {
  type MutableRefObject,
  type RefObject,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useMediaQuery } from 'react-responsive';
import { useScrollLock, useSessionStorage } from 'usehooks-ts';

import type { AlertModalProps } from '../../Member/components/AlertModal';
import type { GlobalModalProps } from '../../Member/components/GlobalModal';
import { useAlertModalStore } from '../../store/alertModalStore';
import { useFloatingButtonStore } from '../../store/floatingButtonStore';
import { useGlobalModalStore } from '../../store/globalModalStore';
import type { FloatingButtonType } from '../../type';
import { handleError, MAX_WIDTH_639PX } from '../common';
import { scrollIntoViewBelowGnb } from '../common/gnbHeight';

import { useHandleFileChange } from './photo';
import useMeQuery from './react-query/user/useMeQuery';
import useClientMediaQuery from './useClientMediaQuery';
import { useHistoryMethod } from './useHistoryMethod';
import { useParams } from './useParams';
import { usePathnameMethod } from './usePathnameMethod';
import { useRouterQuery } from './useRouterQuery';

export function useLocalStorage<T>(key: string, initialValue: T) {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      if (typeof window === 'undefined') return initialValue;

      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      // If error also return initialValue
      console.log(error);
      return initialValue;
    }
  });
  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      if (typeof window === 'undefined') return;

      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      // A more advanced implementation would handle the error case
      console.log(error);
    }
  };
  return [storedValue, setValue] as const;
}

export interface UseExperimentProps {
  experimentId: string;
  customEvent: string;
  totalCaseCount: number;
  initCase?: number;
  locations?: string[];
  condition?: boolean;
}

export function useMobileLineBreak() {
  const isMobile = useClientMediaQuery({ query: MAX_WIDTH_639PX });

  return isMobile ? <br /> : <></>;
}

export const useScroll = () => {
  const [state, setState] = useState({
    x: 0,
    y: 0,
  });
  const onScroll = () => {
    setState({ x: window?.scrollX ?? 0, y: window?.scrollY ?? 0 });
  };
  useEffect(() => {
    window?.addEventListener('scroll', throttle(onScroll, 100));
    return () => window?.removeEventListener('scroll', throttle(onScroll, 100));
  }, []);
  return state;
};

export const usePreventBodyScroll = (condition = true) => {
  /**
   * @link https://usehooks-ts.com/react-hook/use-scroll-lock
   */
  const { lock, unlock } = useScrollLock({
    autoLock: false,
  });

  useEffect(() => {
    if (condition) {
      lock();

      return () => {
        unlock();
      };
    }
  }, [condition]);
};

// Define general type for useWindowSize hook, which includes width and height
export interface Size {
  width: number | undefined;
  height: number | undefined;
}

export const useWindowSize = (): Size => {
  // Initialize state with undefined width/height so server and client renders match
  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
  const [windowSize, setWindowSize] = useState<Size>({
    width: undefined,
    height: undefined,
  });
  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      // Set window width/height to state
      setWindowSize({
        width: window?.innerWidth ?? 0,
        height: window?.innerHeight ?? 0,
      });
    }
    // Add event listener
    window?.addEventListener('resize', handleResize);
    // Call handler right away so state gets updated with initial window size
    handleResize();
    // Remove event listener on cleanup
    return () => window?.removeEventListener('resize', handleResize);
  }, []); // Empty array ensures that effect is only run on mount
  return windowSize;
};

export const useUploadPhotoV2 = () => {
  const [photos, setPhotos] = useState<string[]>([]);

  const onUploadSuccess = (photoURLs: string[]) =>
    setPhotos((prev) => [...prev, ...photoURLs]);

  const { loadingFileCount, handleFileChange } =
    useHandleFileChange(onUploadSuccess);

  const deletePhoto = (index: number) => {
    setPhotos([...photos.slice(0, index), ...photos.slice(index + 1)]);
  };

  return {
    photos,
    setPhotos,
    loadingFileCount,
    handleFileChange,
    deletePhoto,
  };
};

export const useCheckLoggedIn = () => {
  const history = useHistoryMethod();
  const { pathname } = usePathnameMethod();
  const { queryString } = useRouterQuery();
  const { me } = useMeQuery();

  const handleCheckLoggedIn = () => {
    if (!me) {
      history.pushToLogin({ redirectURI: pathname + queryString });
    }
  };

  return handleCheckLoggedIn;
};

export const useKeyEscClose = (
  closeThing: () => void,
  targetKeyCode: number | string = 27, // ESC
) => {
  useEffect(() => {
    const escKeyModalClose = (e: KeyboardEvent) => {
      if (e.keyCode === targetKeyCode) {
        closeThing();
      }
    };
    window.addEventListener('keydown', escKeyModalClose);
    return () => window.removeEventListener('keydown', escKeyModalClose);
  }, []);
};

/**
 * 컴포넌트 렌더링이 완료되어 높이(refHeight), 로딩 상태(isRendered) 를 알 수 있다
 * @param ref 측정을 원하는 ref
 */
export const useRenderedRef = (
  ref: RefObject<HTMLElement | null> | Element | null | undefined,
) => {
  // https://stackoverflow.com/a/61864635
  const [refHeight, setRefHeight] = useState(0);
  const { width } = useWindowSize();
  const isMobile = useMediaQuery({ query: MAX_WIDTH_639PX });

  useEffect(() => {
    if (ref === null || ref === undefined) return;

    let cancelled = false;
    const getHeight = () => {
      const targetElement = 'current' in ref ? ref.current : ref;

      if (!targetElement || !targetElement.clientHeight) {
        if (!cancelled) {
          requestAnimationFrame(getHeight);
        }
      } else {
        setRefHeight(targetElement.clientHeight);
      }
    };
    getHeight();
    return () => {
      cancelled = true;
    };
  }, [width, isMobile]);

  return { refHeight, isRendered: Boolean(refHeight) };
};

export const useIntersecting = (
  targetRef: React.MutableRefObject<Element | null>,
  options?: IntersectionObserverInit,
  condition = true,
  dependencies?: any[],
) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const isMobie = useMediaQuery({ query: MAX_WIDTH_639PX });

  useEffect(() => {
    if (!condition) return;
    let observer: IntersectionObserver;
    if (targetRef.current) {
      observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
        } else {
          setIsIntersecting(false);
        }
      }, options);
      observer.observe(targetRef.current);
    }
    return () => observer?.disconnect();
  }, [options, isMobie, condition, ...(dependencies ?? [])]);

  return isIntersecting;
};

/**
 * Ref가 resized되면 업데이트 된 높이를 가져올 수 있음
 */
export const useGetResizedRefSize = (
  targetRef:
    | React.MutableRefObject<Element | null>
    | Element
    | null
    | undefined,
) => {
  const [targetRefSize, setTargetRefSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    let observer: ResizeObserver;
    if (targetRef === null || targetRef === undefined) return;

    const targetElement =
      'current' in targetRef ? targetRef.current : targetRef;

    observer = new ResizeObserver(
      debounce(([entry]) => {
        setTargetRefSize({
          width: entry.target.clientWidth,
          height: entry.target.clientHeight,
        });
      }, 100),
    );

    targetElement && observer.observe(targetElement);

    return () => {
      targetElement && observer.unobserve(targetElement);
    };
  }, [targetRef]);

  return targetRefSize;
};

export type openAlertModalType = (
  arg: AlertModalProps | string,
  floatingButtonAfterClick?: FloatingButtonType,
) => void;

/** @description 전역알림모달을 생성하는 훅 */
export const useAlertModal = () => {
  const setAlertModal = useAlertModalStore.use.setAlertModal();
  const floatingButton = useFloatingButtonStore.use.floatingButton();
  const setFloatingButton = useFloatingButtonStore.use.setFloatingButton();

  /**
   * @param floatingButtonAfterCloseModal
   * 전역모달이 닫힌 후 띄울 FloatingButton (type: FloatingButtonType)
   * 지정하지 않으면 열리기 전의 상태를 유지
   */
  const closeAlertModal = useCallback(
    (floatingButtonAfterCloseModal?: FloatingButtonType) => {
      setAlertModal(null);
      setFloatingButton(
        floatingButtonAfterCloseModal
          ? floatingButtonAfterCloseModal
          : floatingButton,
      );
    },
    [setAlertModal, setFloatingButton, floatingButton],
  );

  /**
   * @param arg AlertModal 의 props 전달
   * @param floatingButtonAfterClick 모달이 닫힌 후 띄울 FloaingButton (type: FloatingButtonType)
   */
  const openAlertModal = useCallback(
    (
      arg: AlertModalProps | string,
      floatingButtonAfterClick?: FloatingButtonType,
    ) => {
      const openedUrl = window.location.href;
      let alertModalProps: AlertModalProps;
      if (typeof arg === 'string') {
        alertModalProps = { content: arg };
      } else {
        alertModalProps = { ...arg };
      }

      const handleModalAction = (action?: () => void | Promise<void>) => {
        if (openedUrl === window.location.href) {
          action?.();
        }
        closeAlertModal(floatingButtonAfterClick);
      };

      setAlertModal({
        titleIcon: alertModalProps.titleIcon,
        title: alertModalProps.title,
        onClose: () => handleModalAction(alertModalProps.onClose),
        onComplete: () => handleModalAction(alertModalProps.onComplete),
        onCancel: () => handleModalAction(alertModalProps.onCancel),
        buttonText: alertModalProps.buttonText,
        hasConfirmIcon: alertModalProps.hasConfirmIcon,
        hasCancelButton: alertModalProps.hasCancelButton,
        cancelButtonText: alertModalProps.cancelButtonText,
        cancelButtonFlexBasis: alertModalProps.cancelButtonFlexBasis,
        hasButtonGroup: alertModalProps.hasButtonGroup,
        innerPadding: alertModalProps.innerPadding,
        cancelButtonColor: alertModalProps.cancelButtonColor,
        cancelButtonTextColor: alertModalProps.cancelButtonTextColor,
        completeButtonColor: alertModalProps.completeButtonColor,
        width: alertModalProps.width,
        content: alertModalProps.content,
        zIndex: alertModalProps.zIndex,
        backgroundGreenFilter: alertModalProps.backgroundGreenFilter,
        backdropBlur: alertModalProps.backdropBlur,
      });
    },
    [setAlertModal, closeAlertModal],
  );

  /**
   * 확인 모달을 연다
   * - 확인 버튼을 누르면 true, 취소 버튼을 누르면 false를 반환한다
   */
  const openConfirmModal = useCallback(
    async (
      arg: AlertModalProps,
      floatingButtonAfterClick?: FloatingButtonType,
    ): Promise<boolean> => {
      return await new Promise((resolve) => {
        openAlertModal(
          {
            ...arg,
            onComplete: () => {
              arg.onComplete?.();
              resolve(true);
            },
            onCancel: () => {
              arg.onCancel?.();
              resolve(false);
            },
            onClose: () => {
              arg.onClose?.();
              resolve(false);
            },
          },
          floatingButtonAfterClick,
        );
      });
    },
    [openAlertModal],
  );

  return { openAlertModal, closeAlertModal, openConfirmModal };
};

export const useGlobalModal = () => {
  const setGlobalModal = useGlobalModalStore.use.setGlobalModal();
  const floatingButton = useFloatingButtonStore.use.floatingButton();
  const setFloatingButton = useFloatingButtonStore.use.setFloatingButton();

  /**
   * @param floatingButtonAfterCloseModal
   * 전역모달이 닫힌 후 띄울 FloatingButton (type: FloatingButtonType)
   * 지정하지 않으면 열리기 전의 상태를 유지
   */
  const closeGlobalModal = (
    floatingButtonAfterCloseModal?: FloatingButtonType,
  ) => {
    setGlobalModal(null);
    setFloatingButton(
      floatingButtonAfterCloseModal
        ? floatingButtonAfterCloseModal
        : floatingButton,
    );
  };

  const openGlobalModal = (
    arg: GlobalModalProps,
    floatingButtonAfterClick?: FloatingButtonType,
  ) => {
    setGlobalModal({
      modalContent: arg.modalContent,
      modalContentStyle: arg.modalContentStyle,
      onClose: () => {
        arg.onClose?.();
        closeGlobalModal(floatingButtonAfterClick);
      },
      bgColor: arg.bgColor,
      preventBackgroundClick: arg.preventBackgroundClick,
    });
  };

  return { openGlobalModal, closeGlobalModal };
};

export const useClickOutside = (
  ref: MutableRefObject<any>,
  onClick: (e?: MouseEvent) => void,
) => {
  const handleClick = (e: MouseEvent) => {
    if (ref.current && !ref.current.contains(e.target)) {
      onClick(e);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClick, { capture: true });
    return () => {
      document.removeEventListener('click', handleClick, { capture: true });
    };
  });
};

/** 폼 검증 실패 시 첫 에러 섹션(id)으로 상단 크롬에 가리지 않게 스크롤한다 */
export const useScrollToErrorSection = <T extends {}>() => {
  const scrollToErrorSection = (
    needToCheck: { id: string; content: T }[],
    extraExceptHeight?: number,
  ) => {
    scrollIntoViewBelowGnb({
      targetEl: document.querySelector(`#${needToCheck[0].id}`),
      extraOffsetPx: extraExceptHeight ?? 0,
    });
  };

  return scrollToErrorSection;
};

/**
 * @description 뒤로가기 동작을 막고 callBack 함수를 실행한다
 *
 * @param condition true면 로직을 동작하지 않는다
 * @example
 * usePreventGoBack(() => {
 *   openAlertModal({
 *     title: '채소 품목 미확정',
 *     onComplete: () => history.push('/mypages'),
 *   });
 * });
 */
export const usePreventGoBack = (
  callBack: () => void,
  condition: boolean,
  deps?: any[],
) => {
  const { pathname } = usePathnameMethod();
  const [pathnames, setPathnames] = useSessionStorage<string[]>(
    'usePreventGoBack',
    [],
  );

  /**
   * window?.history.pushState는 뒤로가기를 막기위해 필요하다. (이하 '뒤로가기 방어코드'로 정의)
   * 'popstate' 이벤트가 동작하기 위해 필요하다
   * -> 만약 없다면, 'popstate'이벤트가 동작하기 전 이탈을 하게 된다.
   *
   * 세션스토리지에 usePreventGoBack이 마운트될때, pathname을 추가하고
   * 커마 -> 품목 -> 커마 진입 하면서 '뒤로가기 방어코드' 계속 쌓이는 것을 방지한다.
   * -> 만약 이렇게 하지 않는다면, 뒤로가기를 여러번 방어하게 된다. (안좋은 UX)
   */
  useEffect(() => {
    if (pathnames.includes(pathname)) return;

    window?.history.pushState(null, '', window?.location.href);

    setPathnames((prev) => [...prev, pathname]);
  }, []);

  useEffect(() => {
    let isCallbackExecuted = false;

    const preventGoBack = () => {
      if (isCallbackExecuted) return;
      callBack();
      setPathnames((prev) => prev.filter((path) => path !== pathname));
      isCallbackExecuted = true;
    };

    (() => {
      if (condition) return;
      window?.addEventListener('popstate', preventGoBack);
    })();

    return () => {
      window?.removeEventListener('popstate', preventGoBack);
    };
  }, [condition, ...(deps ?? [])]);
};

export const useCheckElementOverViewportHeight = (
  ref: React.MutableRefObject<HTMLElement | null>,
) => {
  const { height } = useWindowSize();

  const [isOverThanViewportHeight, setIsOverThanViewportHeight] =
    useState(false);

  useEffect(() => {
    if (!height) return;
    if (ref.current) {
      if (ref.current?.clientHeight > height) {
        setIsOverThanViewportHeight(true);
      } else {
        setIsOverThanViewportHeight(false);
      }
    }
  }, [height]);

  return isOverThanViewportHeight;
};

/**
 * @description
 * 드래그로 움직이는 캐러셀을 PC에서 좌,우 컨트롤러로 조작할 수 있습니다.
 *
 * ```ts
 * // PC에서 좌, 우로 이동가능한 컨트롤러
 * <PrevNextGalleryController
 *   onPrevClick={moveScrollTo('prev')}
 *   onNextClick={moveScrollTo('next')}
 *   hasPrevPage={scrollPercent > 0}
 *   hasNextPage={scrollPercent < 100}
 * />
 * ```
 *
 * @example
 * const { scrollPercent, moveScrollTo } = useHorizontalScroll({
 *    childrenWidth: 154,
 *    childrenMargin: 8,
 *    scrollElement,
 *  });
 */
export const useHorizontalScroll = ({
  childrenWidth,
  childrenMargin,
  scrollElement,
}: {
  childrenWidth: number;
  childrenMargin: number;
  scrollElement: HTMLElement | null;
}) => {
  const [scrollPercent, setScrollPercent] = useState(0);

  const hasScroll = scrollElement
    ? scrollElement.scrollWidth - scrollElement.clientWidth > 0
    : false;

  const moveScrollTo = (direction: 'prev' | 'next') => () => {
    if (scrollElement === null) return;

    const childrenWidthMargin = childrenWidth + childrenMargin;

    // scrollRef가 화면에 보이는 길이 / childrenWidthMargin
    const remainCount = Math.floor(
      (scrollElement?.clientWidth ?? 0) / childrenWidthMargin,
    );

    // 얼마나 스크롤을 움직일 것인지
    const movePixels = remainCount * childrenWidthMargin;

    const scrollToLeftAmount =
      scrollElement.scrollLeft + // 현재 스크롤 위치
      (direction === 'prev' ? -movePixels : movePixels);

    scrollElement.scrollTo({ left: scrollToLeftAmount, behavior: 'smooth' });
  };

  useEffect(() => {
    if (!scrollElement) return;

    const onScroll = () =>
      setScrollPercent(
        (100 * scrollElement.scrollLeft) /
          (scrollElement.scrollWidth - scrollElement.clientWidth),
      );

    scrollElement.addEventListener('scroll', throttle(onScroll, 100));

    return () =>
      scrollElement.removeEventListener('scroll', throttle(onScroll, 100));
  }, [scrollElement]);

  return { scrollPercent, moveScrollTo, hasScroll };
};

/**
 * history 뒤로가기/앞으로가기를 감지해서 callback을 실행시켜준다.
 */
export const usePopstateCloseModal = (callback: () => void) => {
  useEffect(() => {
    window?.addEventListener('popstate', callback);
    return () => {
      window?.removeEventListener('popstate', callback);
    };
  }, [callback]);
};

export const useIdParam = () => {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  return isNaN(id) ? undefined : id;
};
